import { select, call, takeLatest, put } from 'redux-saga/effects';

import { SubstitutionActions } from '../constants';
import { SubstitutionApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteSubstitutionAction,
    GetSubstitutionAction,
    GetSubstitutionsAction,
    PersistSubstitutionAction,
    CreateFilesSubstitutionAction,
    GetFilesAction,
    DeleteFileSubstitutionAction,
} from '../actions/substitution-actions';
import sortingService from '../../services/sorting';
import {
    handleError,
    resolveApiCall,
    ResolverApiFailure,
    ResolverApiSuccess,
} from '../../services/api-handlers/api-resolver';
import { SubmissionError } from 'redux-form';
import { groupData } from 'src/helpers/substitution';
import { makeFormErrors } from '../../components/redux-form/utils/make-form-errors';
import { convertValuesToFormData } from '../../components/redux-form/utils/values-to-form-data';
import { push } from 'connected-react-router';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';

function* handleFetchSubstitutions(action: GetSubstitutionsAction) {
    const oldData = yield select((state: StoreState) => state.substitutions.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        SubstitutionApi.getSubstitutions({
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchSubstitution(action: GetSubstitutionAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => SubstitutionApi.getSubstitution(id));
}

function* handleDeleteSubstitution(action: DeleteSubstitutionAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => SubstitutionApi.deleteSubstitution(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handlePersistSubstitution(action: PersistSubstitutionAction) {
    const { payload } = action;
    const {
        data,
        //     files,
        callbacks,
    } = payload;
    const groupedData = groupData(data);
    yield resolveApiCall(
        action,
        () => SubstitutionApi.createSubstitution(groupedData),
        function* (data: ResolverApiSuccess) {
            /*  const actionFiles: CreateFilesSubstitutionAction = {
                type: SubstitutionActions.CREATE_FILES_SUBSTITUTION,
                payload: {
                    id: data.payload.userId,
                    files,
                    callbacks,
                },
            };
            yield call(handleCreateFilesSubstitution, actionFiles);*/
            yield call(callbacks.resolve);
            yield put(push(Router.generate(routes.SEANCES_LIST)));
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                yield call(
                    callbacks.reject,
                    result.error instanceof SubmissionError
                        ? result.error
                        : new SubmissionError({
                              _error: result.error.message ?? 'Incorrect response',
                              ...makeFormErrors(result.error.response?.data),
                          }),
                );
            }
            handleError(result);
        },
    );
}

function* handleGetFilesSubstitution(action: GetFilesAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => SubstitutionApi.getFilesSubstitution(id));
}

function* handleCreateFilesSubstitution(action: CreateFilesSubstitutionAction) {
    const {
        payload: { id, files, callbacks },
    } = action;

    for (let i = 0; i < files.length; i++) {
        if (files[i].id || files[i]?.attach) {
            continue;
        }
        const formData = convertValuesToFormData({ user: id, attachFile: files[i] }, '');
        yield resolveApiCall(
            action,
            () => SubstitutionApi.createFilesSubstitution(formData),
            function* (data: ResolverApiSuccess) {
                yield call(callbacks.resolve);
            },
            function* (result: ResolverApiFailure) {
                if (result.error && result.error.response && result.error.response.status) {
                    yield call(
                        callbacks.reject,
                        result.error instanceof SubmissionError
                            ? result.error
                            : new SubmissionError({
                                  _error: result.error.message ?? 'Incorrect response',
                                  ...makeFormErrors(result.error.response?.data),
                              }),
                    );
                }
                handleError(result);
            },
        );
    }
}

function* handleDeleteFileSubstitution(action: DeleteFileSubstitutionAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => SubstitutionApi.deleteFileSubstitution(id),
        function (data) {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(SubstitutionActions.FETCH_SUBSTITUTIONS, handleFetchSubstitutions);
    yield takeLatest(SubstitutionActions.FETCH_SUBSTITUTION, handleFetchSubstitution);
    yield takeLatest(SubstitutionActions.DELETE_SUBSTITUTION, handleDeleteSubstitution);
    yield takeLatest(SubstitutionActions.PERSIST_SUBSTITUTION, handlePersistSubstitution);
    yield takeLatest(SubstitutionActions.DELETE_FILE_SUBSTITUTION, handleDeleteFileSubstitution);
    yield takeLatest(SubstitutionActions.CREATE_FILES_SUBSTITUTION, handleCreateFilesSubstitution);
    yield takeLatest(SubstitutionActions.FETCH_FILES_SUBSTITUTION, handleGetFilesSubstitution);
}
